/**
 * Omasi Collection — Cart recommendations
 * Ported from the Claude Design spec "Cart Recommendations.dc.html".
 *
 * Four-tier recommendation ladder, evaluated in order until the slots are full:
 *
 *   1. Curated pairs     — complementary products set in Search & Discovery, ranked by the
 *                          category affinity model below (see AFFINITY).
 *   2. Bought together   — Shopify's `related` intent, same affinity ranking applied.
 *   3. Easy add          — a fallback collection, also affinity-ranked.
 *   4. Threshold check   — reorders the winners so at least one is priced to carry
 *                          the cart over the free-shipping threshold.
 *
 * Always excluded: products already in the cart, sold-out products, gift cards, anything the
 * affinity model rejects, and men's pieces when the bag contains none.
 *
 * This module keeps strictly to itself: it does not patch globals and does not broadcast
 * events other theme components listen for. It is loaded from snippets/stylesheets.liquid
 * so it is running before the cart drawer is re-rendered.
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

/* ------------------------------------------------------------------ helpers */

/**
 * Shopify returns prices as integer cents from /recommendations/products.json but as
 * decimal strings ("79.00") from /collections/<handle>/products.json. Normalise to cents
 * so the two sources can be compared and sorted together.
 * @param {number|string} value
 * @returns {number} price in cents
 */
function toCents(value) {
  if (typeof value === 'number') return Math.round(value);
  if (typeof value !== 'string') return 0;
  return String(value).includes('.') ? Math.round(parseFloat(value) * 100) : parseInt(value, 10) || 0;
}

/**
 * Format using the shop's own money format string rather than Intl, so recommendation
 * prices match the cart line items exactly. Intl with an "en" locale rendered €59.00 while
 * Shopify rendered the cart line as €79,00 — same page, two separators.
 * Shopify's placeholders: https://shopify.dev/docs/api/liquid/filters/money
 */
function groupNumber(value, thousands, decimal, decimals) {
  const parts = value.toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
}

function formatMoney(cents, format) {
  const value = cents / 100;
  const fmt = typeof format === 'string' ? format : '';

  // Longest placeholder names first — "amount" is a substring of the others.
  const rules = [
    ['amount_no_decimals_with_comma_separator', '.', ',', 0],
    ['amount_with_comma_separator', '.', ',', 2],
    ['amount_with_apostrophe_separator', "'", '.', 2],
    ['amount_no_decimals', ',', '.', 0],
    ['amount', ',', '.', 2],
  ];

  for (const [name, thousands, decimal, decimals] of rules) {
    if (fmt.indexOf(name) > -1) {
      const token = new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}');
      return fmt.replace(token, groupNumber(value, thousands, decimal, decimals));
    }
  }

  // No usable format string — fall back to the shop currency.
  return groupNumber(value, ' ', ',', 2) + ' ' + CURRENCY;
}

async function getJSON(url) {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(url + ' -> ' + res.status);
  return res.json();
}

/** Normalise a product from either JSON source into one internal shape. */
function normalizeProduct(p, reasonKey, reasonContext) {
  const variants = (p.variants || []).map((v) => ({
    id: v.id,
    title: v.title,
    available: v.available !== false,
    price: toCents(v.price),
  }));
  const inStock = variants.filter((v) => v.available);
  const image =
    p.featured_image ||
    (p.images && p.images[0] && (p.images[0].src || p.images[0])) ||
    (p.media && p.media[0] && p.media[0].preview_image && p.media[0].preview_image.src) ||
    null;

  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    type: p.type || p.product_type || '',
    url: ROOT.replace(/\/$/, '') + '/products/' + p.handle,
    image: image,
    price: inStock.length ? Math.min.apply(null, inStock.map((v) => v.price)) : toCents(p.price),
    variants: inStock,
    available: inStock.length > 0,
    reasonKey: reasonKey,
    reasonContext: reasonContext || '',
    mens: isMens(p),
  };
}

/** Resize a Shopify CDN image URL without clobbering existing query params. */
function sizedImage(url, width) {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', String(width));
    return u.toString();
  } catch (e) {
    return url;
  }
}

/* ------------------------------------------------------- the recommendation ladder */

/**
 * Category affinity — how Omasi customers actually shop, per four years of order history
 * and Jessi's read of the physical store.
 *
 *   Bracelets  people STACK. A bracelet in the bag means more bracelets, then a ring.
 *   Necklaces  a different buyer entirely. Buys alone, is not a bracelet person, may add a ring.
 *   Earrings   also sells alone; the natural companion is a ring.
 *   Rings      the Barbels are a price-led easy add. They stack with each other and go with anything.
 *
 * Listed in priority order — earlier entries score higher. A category absent from a list is
 * rejected outright, which is what stops necklace buyers being shown bracelets.
 */
const AFFINITY = {
  Bracelets: ['Bracelets', 'Rings'],
  Necklaces: ['Rings', 'Necklaces'],
  Earrings: ['Rings', 'Earrings'],
  Rings: ['Rings', 'Bracelets'],
};

/**
 * Men's and women's pieces must never be cross-recommended. Every men's product in the
 * catalogue carries a "Men Personalised" tag, so that is the primary signal; the title check
 * is a backstop in case an endpoint omits tags ("Nazar Men", "Men's no.06"). The \b guard
 * matters — without it "Women" would match on its own last three letters.
 */
function isMens(product) {
  if (!product) return false;
  const tags = Array.isArray(product.tags) ? product.tags : [];
  if (tags.some((t) => /^men\b/i.test(String(t).trim()))) return true;
  return /\bmen'?s?\b/i.test(String(product.title || product.product_title || ''));
}

/**
 * A designed set (Luna necklace/earrings/ring, the Barbel family) beats the category rule —
 * "completes the set" is a stronger reason to buy than any affinity heuristic. Detected by a
 * shared leading word in the product title, which is how Omasi names its sets.
 */
function setName(title) {
  const first = String(title || '').trim().split(/\s+/)[0] || '';
  return first.length >= 3 ? first.toLowerCase() : '';
}

/**
 * @returns {number} higher is better; -1 rejects the candidate outright.
 */
function affinityScore(cartType, candidateType, cartTitle, candidateTitle) {
  const set = setName(cartTitle);
  if (set && set === setName(candidateTitle) && cartTitle !== candidateTitle) return 100;

  const order = AFFINITY[cartType];
  if (!order) return 10; // Cart item has no category — don't filter on a guess.
  if (!candidateType) return 1; // Uncategorised candidate — allow, but last.

  const index = order.indexOf(candidateType);
  return index === -1 ? -1 : 50 - index * 10;
}

/**
 * Module-level cache. The theme re-renders the cart drawer on every cart change, which
 * destroys and recreates this element. A per-instance cache was therefore thrown away each
 * time, so every re-open refetched everything and the module appeared blank for a second —
 * which is why the recommendations "sometimes" showed up. Per-product recommendation
 * responses are stable, so caching them across re-renders is safe.
 */
const CACHE = new Map();

class RecommendationEngine {
  constructor(settings) {
    this.settings = settings;
    this.cache = CACHE;
  }

  async fetchIntent(productId, intent) {
    const key = productId + ':' + intent;
    if (this.cache.has(key)) return this.cache.get(key);
    const url =
      ROOT.replace(/\/$/, '') +
      '/recommendations/products.json?product_id=' +
      encodeURIComponent(productId) +
      '&intent=' +
      intent +
      '&limit=10';
    const promise = getJSON(url)
      .then((d) => d.products || [])
      .catch(() => []);
    this.cache.set(key, promise);
    return promise;
  }

  /**
   * /cart.js does not return product tags, so fetch the full product for each cart line.
   * Needed to tell a men's piece from a women's one.
   */
  async fetchProductDetail(handle) {
    if (!handle) return null;
    const key = 'product:' + handle;
    if (this.cache.has(key)) return this.cache.get(key);
    const url = ROOT.replace(/\/$/, '') + '/products/' + handle + '.js';
    const promise = getJSON(url).catch(() => null);
    this.cache.set(key, promise);
    return promise;
  }

  async fetchFallback() {
    const handle = this.settings.fallbackCollection;
    if (!handle) return [];
    if (this.cache.has('fallback')) return this.cache.get('fallback');
    const url = ROOT.replace(/\/$/, '') + '/collections/' + handle + '/products.json?limit=30';
    const promise = getJSON(url)
      .then((d) => d.products || [])
      .catch(() => []);
    this.cache.set('fallback', promise);
    return promise;
  }

  /**
   * @param {object} cart - the /cart.js payload
   * @returns {Promise<Array>} ordered, de-duplicated recommendations
   */
  async build(cart) {
    const limit = this.settings.limit;
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const picked = new Map();
    const excluded = this.settings.excludeHandles;

    const consider = (product) => {
      if (picked.size >= limit) return;
      if (!product.available) return;
      if (inCart.has(product.id)) return;
      if (picked.has(product.id)) return;
      // Gift cards and anything explicitly excluded never make sense as an upsell.
      if (/gift-?card/i.test(product.handle)) return;
      if (excluded.includes(product.handle)) return;
      if (!cartHasMens && product.mens) return;
      picked.set(product.id, product);
    };

    // Most recently added cart item first — that is what the shopper is thinking about.
    const sources = cart.items.slice().reverse();

    // Every request goes out at once. These used to run sequentially, one product at a
    // time per tier, which meant a cart of three items fired seven round trips back to
    // back before anything rendered.
    const [complementary, related, fallback, details] = await Promise.all([
      Promise.all(sources.map((i) => this.fetchIntent(i.product_id, 'complementary'))),
      Promise.all(sources.map((i) => this.fetchIntent(i.product_id, 'related'))),
      this.fetchFallback(),
      Promise.all(sources.map((i) => this.fetchProductDetail(i.handle))),
    ]);

    // If nothing in the bag is a men's piece, no men's piece may be suggested. The reverse is
    // deliberately not enforced: rings are unisex and Jessi sells them to everyone.
    const cartHasMens = sources.some((item, i) => isMens(details[i] || item));

    // Best affinity score for a candidate against everything currently in the bag.
    const bestScore = (candidate) => {
      let best = -1;
      sources.forEach((item) => {
        const score = affinityScore(
          item.product_type,
          candidate.type,
          item.product_title,
          candidate.title
        );
        if (score > best) best = score;
      });
      return best;
    };

    // Score a tier's candidates by category affinity, best first, dropping rejects.
    const rank = (lists, reasonKey) => {
      const scored = [];
      sources.forEach((item, index) => {
        (lists[index] || []).forEach((raw) => {
          const candidate = normalizeProduct(raw, reasonKey, item.product_title);
          const score = affinityScore(
            item.product_type,
            candidate.type,
            item.product_title,
            candidate.title
          );
          if (score < 0) return;
          if (score === 100) {
            // Relabel as a set completion — a stronger reason than "pairs with".
            candidate.reasonKey = 'set';
            candidate.reasonContext =
              setName(item.product_title).charAt(0).toUpperCase() +
              setName(item.product_title).slice(1);
          }
          scored.push({ score, candidate });
        });
      });
      return scored.sort((a, b) => b.score - a.score).map((s) => s.candidate);
    };

    // ---- Tier 1: curated complementary products, ordered by category affinity ----
    rank(complementary, 'layering').forEach(consider);

    // ---- Tier 2: frequently bought together, same affinity rules ----
    rank(related, 'together').forEach(consider);

    // ---- Tier 3: easy add, still audience-aware ----
    // The fallback used to bypass the affinity model entirely, which meant a necklace buyer
    // could be shown a bracelet the moment the curated tiers came up empty. Rings appear in
    // every affinity list, so a rings-led fallback can never be filtered down to nothing.
    (fallback || [])
      .map((raw) => {
        const candidate = normalizeProduct(raw, 'bestseller', '');
        return { score: bestScore(candidate), candidate };
      })
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.candidate)
      .forEach(consider);

    // ---- Tier 4: free-shipping threshold ordering ----
    return this.applyThreshold(Array.from(picked.values()), cart);
  }

  /**
   * If the cart is below the free-shipping threshold, float the cheapest product that
   * would close the gap to the front, so the shopper sees a way to reach it.
   */
  applyThreshold(list, cart) {
    const threshold = this.settings.threshold;
    if (!threshold) return list;
    const remaining = threshold - cart.total_price;
    if (remaining <= 0) return list;

    const closers = list.filter((p) => p.price >= remaining).sort((a, b) => a.price - b.price);
    if (!closers.length) return list;

    const hero = closers[0];
    hero.closesGap = true;
    return [hero].concat(list.filter((p) => p.id !== hero.id));
  }
}

/* ---------------------------------------------------------------- the element */

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.settings = {
      limit: parseInt(this.dataset.limit, 10) || 3,
      threshold: Math.round(parseFloat(this.dataset.threshold || '0') * 100),
      fallbackCollection: this.dataset.fallbackCollection || '',
      showReasons: this.dataset.showReasons !== 'false',
      showProgress: this.dataset.showProgress !== 'false',
      layout: this.dataset.layout || 'grid',
      locale: this.dataset.locale || 'fi',
      moneyFormat: this.dataset.moneyFormat || '',
      excludeHandles: (this.dataset.excludeHandles || '')
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean),
    };
    this.strings = JSON.parse(this.dataset.strings || '{}');
    this.engine = new RecommendationEngine(this.settings);

    this.refresh = this.refresh.bind(this);
    this.bindCartEvents();
    this.refresh();
  }

  disconnectedCallback() {
    if (this.unbind) this.unbind();
  }

  /**
   * Listen for cart changes.
   *
   * This deliberately does NOT patch window.fetch. The first version did, as a fallback for
   * themes without @shopify/events, and it was a bad idea: window.fetch is what the theme
   * itself uses for Section Rendering API calls, so a failed import on any single page left
   * a wrapper in place that interfered with the theme re-rendering its own header.
   * Listening is enough.
   */
  async bindCartEvents() {
    const listeners = [];
    const names = ['cart:update', 'cart:refresh', 'omasi:recs:refresh'];

    try {
      const { StandardEvents } = await import('@shopify/events');
      if (StandardEvents && StandardEvents.cartLinesUpdate) names.push(StandardEvents.cartLinesUpdate);
    } catch (e) {
      // No import map on this page — the plain event names above still cover us.
    }

    names.forEach((name) => {
      document.addEventListener(name, this.refresh);
      listeners.push(() => document.removeEventListener(name, this.refresh));
    });

    this.unbind = () => listeners.forEach((fn) => fn());
  }

  async refresh() {
    try {
      const cart = await getJSON(ROOT.replace(/\/$/, '') + '/cart.js');
      if (!cart.items.length) {
        this.hidden = true;
        return;
      }
      const products = await this.engine.build(cart);
      if (!products.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      this.render(products, cart);
    } catch (e) {
      // Never let a recommendation failure break the cart.
      this.hidden = true;
      console.warn('[cart-recommendations]', e);
    }
  }

  reasonText(product) {
    const t = this.strings;
    if (product.closesGap && t.reason_shipping) return t.reason_shipping;
    switch (product.reasonKey) {
      case 'set':
        return (t.reason_set || t.reason_layering || '').replace('{{ product }}', product.reasonContext);
      case 'layering':
        return (t.reason_layering || '').replace('{{ product }}', product.reasonContext);
      case 'together':
        return (t.reason_together || '').replace('{{ product }}', product.reasonContext);
      default:
        return t.reason_bestseller || '';
    }
  }

  render(products, cart) {
    const t = this.strings;

    const progress =
      this.settings.showProgress && this.settings.threshold ? this.renderProgress(cart) : '';

    const cards = products
      .map((p) => {
        const reason =
          this.settings.showReasons && this.reasonText(p)
            ? '<p class="cart-recs__reason">' + this.escape(this.reasonText(p)) + '</p>'
            : '';

        const picker =
          p.variants.length > 1
            ? '<select class="cart-recs__select" aria-label="' +
              this.escape(t.choose_option || 'Options') +
              '">' +
              p.variants
                .map((v) => '<option value="' + v.id + '">' + this.escape(v.title) + '</option>')
                .join('') +
              '</select>'
            : '';

        const img = p.image
          ? '<img class="cart-recs__image" src="' +
            sizedImage(p.image, 400) +
            '" alt="' +
            this.escape(p.title) +
            '" loading="lazy" width="400" height="500">'
          : '<span class="cart-recs__image cart-recs__image--empty" aria-hidden="true"></span>';

        return (
          '<li class="cart-recs__card" data-product-id="' +
          p.id +
          '">' +
          '<a class="cart-recs__media" href="' +
          p.url +
          '" tabindex="-1" aria-hidden="true">' +
          img +
          '</a>' +
          '<div class="cart-recs__body">' +
          reason +
          '<a class="cart-recs__title" href="' +
          p.url +
          '">' +
          this.escape(p.title) +
          '</a>' +
          '<p class="cart-recs__price">' +
          formatMoney(p.price, this.settings.moneyFormat) +
          '</p>' +
          '<div class="cart-recs__actions">' +
          picker +
          '<button class="cart-recs__add" type="button" data-variant-id="' +
          (p.variants[0] ? p.variants[0].id : '') +
          '">' +
          this.escape(t.add || 'Add') +
          '</button>' +
          '</div>' +
          '</div>' +
          '</li>'
        );
      })
      .join('');

    this.innerHTML =
      progress +
      '<div class="cart-recs__header">' +
      '<h3 class="cart-recs__heading">' +
      this.escape(this.dataset.heading || t.heading || '') +
      '</h3>' +
      '<span class="cart-recs__caption">' +
      this.escape(t.based_on || '') +
      '</span>' +
      '</div>' +
      '<ul class="cart-recs__list" role="list">' +
      cards +
      '</ul>';

    this.wireAddButtons();
  }

  renderProgress(cart) {
    const t = this.strings;
    const remaining = this.settings.threshold - cart.total_price;
    const pct = Math.min(100, (cart.total_price / this.settings.threshold) * 100);
    const label =
      remaining > 0
        ? (t.shipping_remaining || '').replace('{{ amount }}', formatMoney(remaining, this.settings.moneyFormat))
        : t.shipping_unlocked || '';
    return (
      '<div class="cart-recs__progress">' +
      '<p class="cart-recs__progress-label">' +
      this.escape(label) +
      '</p>' +
      '<div class="cart-recs__progress-track"><div class="cart-recs__progress-fill" style="width:' +
      pct +
      '%"></div></div>' +
      '</div>'
    );
  }

  wireAddButtons() {
    this.querySelectorAll('.cart-recs__card').forEach((card) => {
      const select = card.querySelector('.cart-recs__select');
      const button = card.querySelector('.cart-recs__add');
      if (!button) return;

      if (select) {
        select.addEventListener('change', () => {
          button.dataset.variantId = select.value;
        });
      }

      button.addEventListener('click', async () => {
        const id = button.dataset.variantId;
        if (!id) return;
        const original = button.textContent;
        button.disabled = true;
        button.textContent = this.strings.adding || original;
        try {
          const res = await fetch(ROOT.replace(/\/$/, '') + '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('add failed');
          button.textContent = this.strings.added || original;
          // Refresh only our own instances. This used to broadcast a global 'cart:update',
          // which other theme components also listen for — firing extra section re-renders
          // that raced with the theme's own and left the header half-rendered.
          CACHE.clear();
          document.dispatchEvent(new CustomEvent('omasi:recs:refresh', { bubbles: true }));
        } catch (e) {
          button.textContent = this.strings.error || original;
          button.disabled = false;
        }
      });
    });
  }

  escape(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
